/**
 * The query behind the Liberados page.
 *
 * Pulled out of the page because every part of this URL has already been got wrong once in the
 * Operational View, and the failures are silent — an empty list, or a list of the wrong thing.
 * See liberadosQuery.test.ts.
 */

/**
 * "Quem está liberado agora?" vs "quem já esteve?".
 *
 * Complements, served by the same endpoint: `history` keeps the same "it IS a liberação"
 * predicate and inverts the live half (see handlers/liberado_filter.go). An ordinary visit that
 * finished is in neither — the portaria deactivates a walk-in at check-out, and nobody ever
 * cleared them.
 */
export type LiberadosMode = 'live' | 'history';

export interface LiberadosQuery {
  customerId: string;
  page: number;
  pageSize: number;
  search?: string;
  /** Narrows to people holding a clearance for ONE unit. */
  unitId?: string;
  /** Defaults to the live list, which is what every existing caller wants. */
  mode?: LiberadosMode;
}

/** Digits plus the punctuation a CPF carries, and nothing else — same shape test as the mask. */
const CPF_SHAPED = /^[\d.-]+$/;

/**
 * A CPF travels as bare digits.
 *
 * The search box masks one as it is typed ("412.380.788-77", see components/searchInputMask),
 * but `people.cpf` is stored normalized and the server matches the term against the column with
 * a plain ILIKE — so the punctuated form finds nobody while the digits find the person. Only
 * CPF-shaped input is touched; a name, a plate or an address keeps every character.
 */
function serverSearchTerm(raw: string): string {
  return CPF_SHAPED.test(raw) ? raw.replace(/\D/g, '') : raw;
}

export function liberadosEndpoint({ customerId, page, pageSize, search, unitId, mode = 'live' }: LiberadosQuery): string {
  const params = new URLSearchParams({
    // BOTH role types, always. A prestador liberação is written as a `visitor` role carrying
    // `role_metadata.category`, so narrowing this to service_provider returns an empty list — the
    // visitante/prestador split belongs on the rows, not here.
    role_type: 'visitor,service_provider',
    // A clearance that has not started yet, or has just ended, is exactly what somebody opens
    // this page to check.
    include_inactive_roles: 'true',
    // What makes the server answer with clearances rather than every visitor ever registered.
    // `history` is the ended ones; the server widens the role join itself for that mode, since a
    // revoke deactivates the row.
    liberado: mode === 'history' ? 'history' : 'true',
    page: String(page),
    page_size: String(pageSize),
  });

  const term = search?.trim();
  if (term) params.set('search', serverSearchTerm(term));
  // Their clearances for OTHER units still ride along in the payload, so the rows are narrowed
  // again on screen.
  if (unitId) params.set('unit_id', unitId);

  return `/api/v1/customers/${customerId}/people?${params.toString()}`;
}
